// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

//
// COB-263 — the verifier-served confirm embed (`/embed/confirm/:id`).
//
// Rendered inside an <iframe> hosted by the user-app's vault-creation
// modal. Independence comes from origin isolation: this contract summary
// is drawn by the VERIFIER's own origin, never by user-app — the gate
// defends against a compromised user-app (COB-245), so a user-app-rendered
// contract would defeat it.
//
// The flow inverts the `/v/:id` popup: here the verifier is the CHILD and
// the user-app is the PARENT. The embed asks the parent to sign the
// ownership challenge (the parent's key-agent is in-context in the
// creation flow, so no popup); the parent reconstructs the canonical
// `cobault-verifier-ownership-v1:{vault}:{nonce}` message itself. Once
// proven, the embed fetches the independently-stored contract and shows a
// compact, load-bearing summary. Confirm records the ack against the
// stored hash.
//
// The `confirmed` postMessage is ADVISORY: user-app still gates the
// deposit-address reveal server-side on the verifier's ack-status
// (fail-closed). A spoofed `confirmed` reveals nothing.
//

import { useEffect, useRef, useState } from 'preact/hooks';
import { useRoute } from 'preact-iso';
import {
  api,
  type PublicLookupResponse,
  type ProveResponse,
} from '../api.js';
import { formatBtc } from '../format.js';
import { userAppOriginForCurrentVerifier } from '../sign-via-user-app.js';
import {
  confirmedMessage,
  errorMessage,
  isEmbedSignatureErrorFor,
  isEmbedSignatureFor,
  readyMessage,
  requestSignatureMessage,
  resizeMessage,
  type EmbedChildMessage,
  type EmbedSignatureErrorMessage,
  type EmbedSignatureMessage,
} from '../embed-protocol.js';

// ── child-side "ask the parent to sign" helper ─────────────────────
//
// The counterpart to `sign-via-user-app.ts`'s `requestSignatureFromUserApp`
// (which is the popup, verifier-as-child-window). Here we are already the
// child <iframe>, so we postMessage the parent directly and await the
// reply. Never rejects — every failure path resolves a discriminated kind.

export type ParentSignatureResult =
  | { kind: 'ok'; signatureDerHex: string }
  | { kind: 'declined'; reason: string }
  | { kind: 'timeout' }
  | { kind: 'aborted' };

export interface RequestParentSignatureArgs {
  vault: string;
  nonce: string;
  /** The exact user-app origin we accept a reply from and target our
   *  request at. Resolved from the symmetric verifier→user-app map. */
  parentOrigin: string;
  /** Timeout in ms. Default 120 000 (120s) — the parent may need to
   *  unlock a key-agent. */
  timeoutMs?: number;
  signal?: AbortSignal;
  /** Parent-window override (tests). Defaults to `window.parent`. */
  parent?: Pick<Window, 'postMessage'>;
  /** Message-target override (tests). Defaults to `window`. */
  messageTarget?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
}

const DEFAULT_SIGN_TIMEOUT_MS = 120_000;

/**
 * Ask the parent user-app to sign the ownership challenge and await the
 * reply. Origin-pinned both ways: the request is `postMessage`d with
 * `parentOrigin` as `targetOrigin`, and only a reply whose
 * `event.origin === parentOrigin` (and that binds the exact vault + nonce)
 * is accepted. Anything else is ignored.
 */
export function requestSignatureFromParent(
  args: RequestParentSignatureArgs,
): Promise<ParentSignatureResult> {
  const target = args.messageTarget ?? window;
  const parent = args.parent ?? window.parent;
  const timeoutMs = args.timeoutMs ?? DEFAULT_SIGN_TIMEOUT_MS;

  return new Promise<ParentSignatureResult>((resolve) => {
    let settled = false;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

    function cleanup(): void {
      if (settled) return;
      settled = true;
      target.removeEventListener('message', onMessage);
      if (timeoutHandle !== null) clearTimeout(timeoutHandle);
      if (args.signal) args.signal.removeEventListener('abort', onAbort);
    }

    function onMessage(ev: MessageEvent): void {
      // Origin pin FIRST — a matching shape from the wrong origin is
      // still hostile.
      if (ev.origin !== args.parentOrigin) return;
      if (isEmbedSignatureFor(ev.data, args.vault, args.nonce)) {
        cleanup();
        resolve({
          kind: 'ok',
          signatureDerHex: (ev.data as EmbedSignatureMessage).signature_der_hex,
        });
        return;
      }
      if (isEmbedSignatureErrorFor(ev.data, args.vault, args.nonce)) {
        cleanup();
        resolve({
          kind: 'declined',
          reason: (ev.data as EmbedSignatureErrorMessage).reason,
        });
      }
    }

    function onAbort(): void {
      cleanup();
      resolve({ kind: 'aborted' });
    }

    function onTimeout(): void {
      cleanup();
      resolve({ kind: 'timeout' });
    }

    target.addEventListener('message', onMessage);
    timeoutHandle = setTimeout(onTimeout, timeoutMs);
    if (args.signal) {
      if (args.signal.aborted) {
        onAbort();
        return;
      }
      args.signal.addEventListener('abort', onAbort);
    }

    parent.postMessage(
      requestSignatureMessage(args.vault, args.nonce),
      args.parentOrigin,
    );
  });
}

// ── embed state machine ────────────────────────────────────────────

type EmbedUiState =
  | { kind: 'init' }
  | { kind: 'awaiting-signature' }
  | { kind: 'proving' }
  | { kind: 'loading' }
  | { kind: 'review'; data: PublicLookupResponse; session: ProveResponse }
  | { kind: 'confirming'; data: PublicLookupResponse; session: ProveResponse }
  | {
      kind: 'conflict';
      message: string;
      data: PublicLookupResponse;
      session: ProveResponse;
    }
  | { kind: 'confirmed'; atUnix: number; contractHashHex: string }
  | { kind: 'error'; code: string; message: string }
  | { kind: 'not-configured' };

function postToParent(parentOrigin: string, message: EmbedChildMessage): void {
  if (typeof window === 'undefined') return;
  window.parent.postMessage(message, parentOrigin);
}

function signatureDeclinedMessage(reason: string): string {
  // The parent's reason codes are advisory; map the common ones to
  // owner-facing copy, fall back to a generic line otherwise.
  switch (reason) {
    case 'locked':
    case 'no-session':
      return 'The Cobault app could not sign — please make sure you are signed in, then try again.';
    case 'declined':
      return 'Signing was cancelled in the Cobault app.';
    default:
      return 'The Cobault app could not produce a signature. Try again.';
  }
}

/**
 * Drive the handshake once per vault: ready → challenge → ask parent to
 * sign → prove → fetch contract → review. Exported for tests. `signal`
 * aborts on unmount / vault change so a late reply can't set state on a
 * torn-down component.
 */
export async function runEmbedHandshake(
  vaultId: string,
  parentOrigin: string,
  signal: AbortSignal,
  setState: (s: EmbedUiState) => void,
): Promise<void> {
  const fail = (code: string, message: string): void => {
    postToParent(parentOrigin, errorMessage(code, message));
    setState({ kind: 'error', code, message });
  };

  postToParent(parentOrigin, readyMessage(vaultId));

  const challenge = await api.issueChallenge(vaultId);
  if (signal.aborted) return;
  if (!challenge) {
    fail(
      'challenge',
      'Could not start the ownership challenge with the verifier. Try again in a minute.',
    );
    return;
  }

  setState({ kind: 'awaiting-signature' });
  const sig = await requestSignatureFromParent({
    vault: vaultId,
    nonce: challenge.nonce_hex,
    parentOrigin,
    signal,
  });
  if (signal.aborted || sig.kind === 'aborted') return;
  if (sig.kind === 'timeout') {
    fail(
      'signature-timeout',
      'Timed out waiting for the Cobault app to sign. Try again.',
    );
    return;
  }
  if (sig.kind === 'declined') {
    fail('signature-declined', signatureDeclinedMessage(sig.reason));
    return;
  }

  setState({ kind: 'proving' });
  const proved = await api.prove(vaultId, challenge.nonce_hex, sig.signatureDerHex);
  if (signal.aborted) return;
  if (proved.kind !== 'ok') {
    const message =
      proved.kind === 'invalid-signature'
        ? 'The signature did not verify against this vault’s user key.'
        : proved.kind === 'expired-or-used-nonce'
          ? 'The ownership challenge expired. Reload to try again.'
          : proved.kind === 'rate-limited'
            ? 'Too many attempts. Wait a minute and try again.'
            : proved.message;
    fail('prove', message);
    return;
  }

  setState({ kind: 'loading' });
  const full = await api.lookupVaultFull(vaultId, proved.value.session_token);
  if (signal.aborted) return;
  if (full.kind !== 'ok') {
    const message =
      full.kind === 'unauthorized'
        ? 'The ownership session was rejected. Reload to try again.'
        : full.kind === 'not-found'
          ? 'The verifier has no record of this vault.'
          : full.kind === 'rate-limited'
            ? 'Too many requests. Wait a minute and try again.'
            : full.message;
    fail('lookup', message);
    return;
  }

  setState({ kind: 'review', data: full.value, session: proved.value });
}

export function EmbedConfirm() {
  const { params } = useRoute();
  const vaultId = params.id ?? '';
  const parentOrigin = userAppOriginForCurrentVerifier(
    typeof window !== 'undefined' ? window.location.origin : '',
  );
  const [state, setState] = useState<EmbedUiState>({ kind: 'init' });
  const rootRef = useRef<HTMLDivElement>(null);

  // Drive the handshake once per (vault, parent).
  useEffect(() => {
    if (!vaultId) return;
    if (!parentOrigin) {
      setState({ kind: 'not-configured' });
      return;
    }
    setState({ kind: 'init' });
    const controller = new AbortController();
    void runEmbedHandshake(vaultId, parentOrigin, controller.signal, setState);
    return () => controller.abort();
  }, [vaultId, parentOrigin]);

  // Keep the host iframe sized to content: post our height on mount and
  // whenever it changes, so the embed never scrolls inside its own frame.
  useEffect(() => {
    if (!parentOrigin || typeof ResizeObserver === 'undefined') return;
    const el = rootRef.current;
    if (!el) return;
    const post = () =>
      postToParent(
        parentOrigin,
        resizeMessage(Math.ceil(el.getBoundingClientRect().height)),
      );
    post();
    const observer = new ResizeObserver(post);
    observer.observe(el);
    return () => observer.disconnect();
  }, [parentOrigin]);

  async function confirm(
    data: PublicLookupResponse,
    session: ProveResponse,
  ): Promise<void> {
    if (!parentOrigin) return;
    setState({ kind: 'confirming', data, session });
    const result = await api.acknowledgeContract(
      vaultId,
      session.session_token,
      data.contract_hash_hex,
    );
    if (result.kind === 'ok') {
      // Advisory signal to the host; the reveal stays server-gated.
      postToParent(
        parentOrigin,
        confirmedMessage(vaultId, data.contract_hash_hex),
      );
      setState({
        kind: 'confirmed',
        atUnix: result.value.acked_at_unix,
        contractHashHex: data.contract_hash_hex,
      });
      return;
    }
    if (result.kind === 'conflict') {
      setState({ kind: 'conflict', message: result.message, data, session });
      return;
    }
    if (result.kind === 'unauthorized') {
      postToParent(
        parentOrigin,
        errorMessage(
          'session-expired',
          'The ownership session expired before confirmation.',
        ),
      );
      setState({
        kind: 'error',
        code: 'session-expired',
        message: 'The ownership session expired. Reload to confirm again.',
      });
      return;
    }
    const message =
      result.kind === 'rate-limited'
        ? 'Too many requests. Wait a minute and try again.'
        : result.kind === 'not-found'
          ? 'The verifier has no record of this vault.'
          : result.message;
    setState({ kind: 'error', code: 'ack', message });
  }

  return (
    <div class="embed-root" ref={rootRef}>
      <EmbedHeader />
      <EmbedBody state={state} vaultId={vaultId} onConfirm={confirm} />
      <EmbedFooter vaultId={vaultId} />
    </div>
  );
}

function EmbedHeader() {
  return (
    <header class="embed-header">
      <span class="embed-mark">●</span>
      <div class="embed-header-text">
        <div class="embed-header-title">Independent Verifier</div>
        <div class="embed-header-sub">
          Confirm your vault contract as the verifier stored it
        </div>
      </div>
    </header>
  );
}

function EmbedBody({
  state,
  vaultId,
  onConfirm,
}: {
  state: EmbedUiState;
  vaultId: string;
  onConfirm: (data: PublicLookupResponse, session: ProveResponse) => void;
}) {
  switch (state.kind) {
    case 'init':
      return <EmbedStatus text="Connecting to the independent verifier…" />;
    case 'awaiting-signature':
      return (
        <EmbedStatus text="Approve the ownership check in the Cobault app to continue…" />
      );
    case 'proving':
      return <EmbedStatus text="Verifying your ownership…" />;
    case 'loading':
      return <EmbedStatus text="Loading the verifier’s record of your contract…" />;
    case 'review':
    case 'confirming':
    case 'conflict':
      return (
        <div class="embed-review">
          <ContractSummary data={state.data} />
          {state.kind === 'conflict' && (
            <p class="embed-alert" role="alert">
              The contract changed since this loaded. Reload to review the
              updated terms, then confirm again. ({state.message})
            </p>
          )}
          <button
            type="button"
            class="btn btn-primary embed-confirm-btn"
            onClick={() => onConfirm(state.data, state.session)}
            disabled={state.kind === 'confirming'}
          >
            {state.kind === 'confirming'
              ? 'Confirming…'
              : 'Confirm these terms'}
          </button>
          <p class="embed-gate-note">
            Your deposit address is revealed only after you confirm here.
          </p>
        </div>
      );
    case 'confirmed':
      return (
        <div class="embed-confirmed">
          <span class="embed-check">✓</span>
          <p>
            You confirmed these terms on {formatUnix(state.atUnix)}. You can
            return to the app to reveal your deposit address.
          </p>
        </div>
      );
    case 'error':
      return (
        <div class="embed-error" role="alert">
          <p>{state.message}</p>
          <VerifyDirectlyLink vaultId={vaultId} />
        </div>
      );
    case 'not-configured':
      return (
        <div class="embed-error" role="alert">
          <p>
            This confirmation can’t run embedded on this host. Open the
            verifier directly to confirm.
          </p>
          <VerifyDirectlyLink vaultId={vaultId} />
        </div>
      );
  }
}

function EmbedStatus({ text }: { text: string }) {
  return (
    <div class="embed-status" role="status">
      <span class="embed-spinner" aria-hidden="true" />
      <span>{text}</span>
    </div>
  );
}

/** The compact, load-bearing contract summary: the fields a user must
 *  check to catch a malicious payout/refund address before depositing.
 *  The full contract stays one click away via the pop-out. */
function ContractSummary({ data }: { data: PublicLookupResponse }) {
  const { contract } = data;
  return (
    <div class="embed-summary">
      <div class="embed-hash-block">
        <div class="embed-hash-label">Contract hash</div>
        <div class="embed-hash-value mono">{data.contract_hash_hex}</div>
        <p class="embed-hash-help">
          This must match the hash the Cobault app shows for this vault. If
          they differ, do not deposit.
        </p>
      </div>

      <dl class="embed-fields">
        <div class="embed-field">
          <dt>Deposit</dt>
          <dd class="tabular-nums">{formatBtc(contract.principal_sats)} BTC</dd>
        </div>
        <div class="embed-field">
          <dt>Reward</dt>
          <dd class="tabular-nums">{formatBtc(contract.reward_sats)} BTC</dd>
        </div>
        <div class="embed-field">
          <dt>Price levels (USD)</dt>
          <dd>
            short {contract.strikes.short_usd.toLocaleString()} / long{' '}
            {contract.strikes.long_usd.toLocaleString()}
          </dd>
        </div>
        <div class="embed-field">
          <dt>Max loss</dt>
          <dd class="tabular-nums">{contract.max_loss_bps} bps</dd>
        </div>
        <div class="embed-field embed-field-wide">
          <dt>Your payout address</dt>
          <dd class="mono embed-addr">{contract.user_payout_address}</dd>
        </div>
        <div class="embed-field embed-field-wide">
          <dt>Platform refund address</dt>
          <dd class="mono embed-addr">{contract.platform_refund_address}</dd>
        </div>
      </dl>
    </div>
  );
}

function EmbedFooter({ vaultId }: { vaultId: string }) {
  return (
    <footer class="embed-footer">
      <VerifyDirectlyLink vaultId={vaultId} />
    </footer>
  );
}

/** Top-level pop-out to the verifier's own full page — the mitigation for
 *  the iframe address-bar tradeoff. Opens the verifier origin in a new tab
 *  so the user can see `verifier.…cobault.io` in the address bar and the
 *  complete contract. */
function VerifyDirectlyLink({ vaultId }: { vaultId: string }) {
  if (!vaultId) return null;
  return (
    <a
      class="embed-verify-link"
      href={`/v/${encodeURIComponent(vaultId)}`}
      target="_blank"
      rel="noopener"
    >
      Verify independently on the verifier ↗
    </a>
  );
}

function formatUnix(seconds: number): string {
  if (!seconds) return '—';
  const date = new Date(seconds * 1000);
  return `${date.toISOString().replace('T', ' ').replace(/\..*/, '')} UTC`;
}
